/**
 * The neural field: nodes inside the brain, and sparks firing between them.
 *
 * This is the game's main piece of live feedback. Everything here is driven by
 * actual state rather than decoration:
 *
 * - the number of nodes grows as generators are installed, so buying something
 *   visibly adds to the brain rather than only to a number;
 * - sparks fire at a rate derived from real output, from roughly one a minute
 *   at the very start to several a second once the rig is running;
 * - buying anything fires a burst immediately, so a purchase has a physical
 *   consequence you can see.
 *
 * Positions are in fractions of the brain ellipse, so the field tracks the
 * artwork at any size.
 */

const MIN_NODES = 4;
const MAX_NODES = 26;
const MAX_SPARKS = 90;

/** Sparks per minute at the floor and ceiling of the output range. */
const SPARKS_MIN_PER_MIN = 1;
const SPARKS_MAX_PER_MIN = 240;

/**
 * Output decades over which the spark rate ramps from floor to ceiling, and
 * where that ramp starts. The floor is set at the resting output of a brand new
 * jar, so a fresh game really does blink about once a minute.
 */
const RATE_DECADES = 11.5;
const RATE_FLOOR_DECADE = 0.5;

interface Node {
  /** Offsets from the brain centre, in fractions of its radii. */
  x: number;
  y: number;
  /** Drift phase, so the field breathes rather than sitting rigid. */
  phase: number;
  /** Fades after a spark lands here. */
  flash: number;
}

interface Spark {
  from: number;
  to: number;
  /** 0..1 along the link. */
  t: number;
  speed: number;
}

export interface NeuralField {
  nodes: Node[];
  links: Array<[number, number]>;
  sparks: Spark[];
  /** Fractional sparks owed, so rates below one a second still fire. */
  pending: number;
  /** 1 immediately after a purchase, decaying. Drives the buy burst. */
  burst: number;
}

export function createNeuralField(): NeuralField {
  const field: NeuralField = { nodes: [], links: [], sparks: [], pending: 0, burst: 0 };
  growTo(field, MIN_NODES);
  return field;
}

export function resetNeuralField(field: NeuralField): void {
  field.nodes.length = 0;
  field.links.length = 0;
  field.sparks.length = 0;
  field.pending = 0;
  field.burst = 0;
  growTo(field, MIN_NODES);
}

/**
 * How many nodes a rig of this size should show.
 * Log-scaled: the field fills in steadily instead of exploding at tier three.
 */
export function nodesForGenerators(total: number): number {
  const scaled = MIN_NODES + Math.log10(1 + total) * 6;
  return Math.max(MIN_NODES, Math.min(MAX_NODES, Math.round(scaled)));
}

/**
 * Sparks per second for a given output.
 * Log-scaled, because output spans a dozen orders of magnitude and a linear
 * mapping would sit at one extreme or the other for the whole game.
 */
export function sparkRateFor(outputPerSecond: number): number {
  if (!(outputPerSecond > 0)) return 0;
  const decades = Math.log10(outputPerSecond) - RATE_FLOOR_DECADE;
  const t = Math.max(0, Math.min(1, decades / RATE_DECADES));
  const perMinute = SPARKS_MIN_PER_MIN + (SPARKS_MAX_PER_MIN - SPARKS_MIN_PER_MIN) * t;
  return perMinute / 60;
}

function growTo(field: NeuralField, count: number): void {
  while (field.nodes.length < count) {
    // Sunflower placement: even coverage without clumping, and stable because
    // each node's position depends only on its index.
    const i = field.nodes.length;
    const angle = i * 2.399963;
    const radius = Math.sqrt((i + 0.5) / MAX_NODES) * 0.82;
    field.nodes.push({
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius * 0.9,
      phase: (i * 1.7) % (Math.PI * 2),
      flash: 0,
    });
  }
  relink(field);
}

/** Links each node to its two nearest neighbours. */
function relink(field: NeuralField): void {
  field.links.length = 0;
  const n = field.nodes.length;
  for (let i = 0; i < n; i++) {
    const a = field.nodes[i]!;
    const near = field.nodes
      .map((b, j) => ({ j, d: (a.x - b.x) ** 2 + (a.y - b.y) ** 2 }))
      .filter((e) => e.j !== i)
      .sort((p, q) => p.d - q.d)
      .slice(0, 2);
    for (const { j } of near) {
      if (!field.links.some(([p, q]) => (p === i && q === j) || (p === j && q === i))) {
        field.links.push([i, j]);
      }
    }
  }
}

/** Fires `count` sparks immediately. Used to give a purchase a visible kick. */
export function pulseNeural(field: NeuralField, count: number): void {
  for (let i = 0; i < count; i++) spawn(field);
  // Light every node too, so a purchase lands as one event rather than as a
  // few extra sparks lost in the traffic.
  for (const node of field.nodes) node.flash = 1;
  field.burst = 1;
}

function spawn(field: NeuralField): void {
  if (field.links.length === 0 || field.sparks.length >= MAX_SPARKS) return;
  const link = field.links[Math.floor(Math.random() * field.links.length)]!;
  const forward = Math.random() < 0.5;
  field.sparks.push({
    from: forward ? link[0] : link[1],
    to: forward ? link[1] : link[0],
    t: 0,
    speed: 0.55 + Math.random() * 0.7,
  });
}

export interface NeuralInput {
  /** Total generators owned, which sets the size of the field. */
  totalGenerators: number;
  /** Sparks per second, from sparkRateFor(). */
  sparkRate: number;
  dt: number;
  timeSec: number;
  reducedMotion: boolean;
}

export function updateNeural(field: NeuralField, input: NeuralInput): void {
  const wanted = nodesForGenerators(input.totalGenerators);
  if (wanted > field.nodes.length) growTo(field, wanted);

  for (const node of field.nodes) {
    if (node.flash > 0) node.flash = Math.max(0, node.flash - input.dt * 0.75);
  }

  if (field.burst > 0) field.burst = Math.max(0, field.burst - input.dt * 1.6);

  if (input.reducedMotion) {
    field.sparks.length = 0;
    field.burst = 0;
    return;
  }

  field.pending += input.sparkRate * input.dt;
  while (field.pending >= 1) {
    field.pending -= 1;
    spawn(field);
  }
  if (field.pending > 1) field.pending = 1;

  for (let i = field.sparks.length - 1; i >= 0; i--) {
    const spark = field.sparks[i]!;
    spark.t += spark.speed * input.dt;
    if (spark.t >= 1) {
      const target = field.nodes[spark.to];
      if (target !== undefined) target.flash = 1;
      field.sparks.splice(i, 1);
    }
  }
}

export function drawNeural(
  ctx: CanvasRenderingContext2D,
  field: NeuralField,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  input: NeuralInput,
): void {
  const drift = (node: Node): [number, number] => {
    const wobble = input.reducedMotion ? 0 : Math.sin(input.timeSec * 0.9 + node.phase) * 0.012;
    return [cx + (node.x + wobble) * rx, cy + (node.y + wobble) * ry];
  };

  ctx.save();
  // Additive, and warm. The artwork already carries a cool white web and its
  // own lightning; drawing more pale lines over it was invisible - the sparks
  // have to be a colour the picture does not already contain.
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';

  // Arrival flashes: an expanding ring where a spark landed.
  for (const node of field.nodes) {
    if (node.flash <= 0.01) continue;
    const [x, y] = drift(node);
    const grow = 1 - node.flash;
    const r = Math.max(3, rx * 0.075) * (0.5 + grow * 2.6);

    const halo = ctx.createRadialGradient(x, y, 0, x, y, r * 2.4);
    halo.addColorStop(0, `rgba(255, 208, 120, ${(node.flash * 0.55).toFixed(3)})`);
    halo.addColorStop(1, 'rgba(255, 170, 60, 0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(x, y, r * 2.4, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = `rgba(255, 232, 176, ${(node.flash * 0.75).toFixed(3)})`;
    ctx.lineWidth = Math.max(1, rx * 0.012) * node.flash;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Sparks in flight: a bright head with a tapering tail behind it.
  for (const spark of field.sparks) {
    const na = field.nodes[spark.from];
    const nb = field.nodes[spark.to];
    if (na === undefined || nb === undefined) continue;
    const [ax, ay] = drift(na);
    const [bx, by] = drift(nb);

    const head = Math.min(1, spark.t);
    const tail = Math.max(0, spark.t - 0.35);
    const hx = ax + (bx - ax) * head;
    const hy = ay + (by - ay) * head;
    const tx = ax + (bx - ax) * tail;
    const ty = ay + (by - ay) * tail;

    const trail = ctx.createLinearGradient(tx, ty, hx, hy);
    trail.addColorStop(0, 'rgba(255, 150, 40, 0)');
    trail.addColorStop(1, 'rgba(255, 220, 150, 0.95)');
    ctx.strokeStyle = trail;
    ctx.lineWidth = Math.max(3, rx * 0.05);
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(hx, hy);
    ctx.stroke();

    const r = Math.max(3, rx * 0.055);
    const glow = ctx.createRadialGradient(hx, hy, 0, hx, hy, r * 3.5);
    glow.addColorStop(0, 'rgba(255, 244, 214, 0.95)');
    glow.addColorStop(0.35, 'rgba(255, 190, 90, 0.5)');
    glow.addColorStop(1, 'rgba(255, 150, 40, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(hx, hy, r * 3.5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

/**
 * A warm throb across the whole brain, beating at the spark rate.
 *
 * Drawn under the sparks. At one blink a minute it is a slow swell; at a couple
 * hundred it is a continuous glow, so the rig reads as busy from across the room
 * without having to count anything.
 */
/**
 * The shockwave a purchase sends through the brain. Unmistakable on purpose:
 * installing hardware should be felt, not just read off a counter.
 */
export function drawBurst(
  ctx: CanvasRenderingContext2D,
  field: NeuralField,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  if (field.burst <= 0.01) return;
  const grow = 1 - field.burst;
  const radius = Math.max(rx, ry) * (0.35 + grow * 1.5);

  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  const wash = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  wash.addColorStop(0, `rgba(255, 226, 160, ${(field.burst * 0.42).toFixed(3)})`);
  wash.addColorStop(1, 'rgba(255, 160, 60, 0)');
  ctx.fillStyle = wash;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = `rgba(255, 240, 200, ${(field.burst * 0.85).toFixed(3)})`;
  ctx.lineWidth = Math.max(2, rx * 0.05) * field.burst;
  ctx.beginPath();
  ctx.ellipse(cx, cy, radius, radius * (ry / rx), 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

export function drawBrainPulse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  input: NeuralInput,
): void {
  if (input.reducedMotion) return;
  const busy = Math.min(1, input.sparkRate / 3);
  const beat = 0.5 + 0.5 * Math.sin(input.timeSec * (1.1 + input.sparkRate * 0.4));
  // A floor, so even an idle jar visibly breathes rather than looking switched
  // off, and it climbs from there with output.
  const strength = 0.16 + busy * 0.42 * (0.5 + beat * 0.5);
  const radius = Math.max(rx, ry) * 1.25;

  const grad = ctx.createRadialGradient(cx, cy, radius * 0.15, cx, cy, radius);
  grad.addColorStop(0, `rgba(255, 196, 110, ${(strength * 0.5).toFixed(3)})`);
  grad.addColorStop(0.6, `rgba(255, 150, 60, ${(strength * 0.22).toFixed(3)})`);
  grad.addColorStop(1, 'rgba(255, 120, 40, 0)');

  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
